import React, { useState } from "react";
import BackgroundRings from "../components/BackgroundRings";
import FadeIn from "../components/FadeIn";
import LocationColumn from "../components/LocationColumn";
import AnimalRecoverySection from "../components/AnimalRecoverySection";
import { Plus, Minus } from "lucide-react";
import { waUrl } from "../lib/constants";

const FAQ_ITEMS = [
    {
        q: "What should I wear?",
        a: "Comfortable clothing. You will be lying down.",
    },
    {
        q: "How long is a session?",
        a: "20–30 minutes for demo. 60 minutes for full session. 90 minutes for HNWI private sessions.",
    },
    {
        q: "Where are you located?",
        a: "Al Hamra, Ras Al Khaimah. Al Furjan, Dubai. In-villa sessions available UAE-wide.",
    },
];

const FAQItem = ({ q, a, idx }) => {
    const [open, setOpen] = useState(idx === 0);
    return (
        <div
            data-testid={`faq-item-${idx + 1}`}
            style={{
                borderTop:
                    idx === 0
                        ? "1px solid rgba(201,162,39,0.25)"
                        : "1px solid rgba(201,162,39,0.1)",
                padding: "28px 0",
            }}
        >
            <button
                onClick={() => setOpen(!open)}
                data-testid={`faq-toggle-${idx + 1}`}
                style={{
                    width: "100%",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 16,
                    background: "transparent",
                    border: "none",
                    color: "#ffffff",
                    fontFamily: "Playfair Display, serif",
                    fontSize: "clamp(20px, 2.4vw, 28px)",
                    fontWeight: 400,
                    textAlign: "left",
                    cursor: "pointer",
                    padding: 0,
                    lineHeight: 1.3,
                }}
            >
                {q}
                <span
                    style={{
                        color: "#c9a227",
                        flexShrink: 0,
                        display: "inline-flex",
                    }}
                >
                    {open ? <Minus size={22} /> : <Plus size={22} />}
                </span>
            </button>
            <div
                style={{
                    maxHeight: open ? 240 : 0,
                    overflow: "hidden",
                    transition: "max-height 0.4s ease, margin-top 0.4s ease",
                    marginTop: open ? 16 : 0,
                }}
            >
                <p
                    style={{
                        color: "#a8a8a8",
                        fontSize: 17,
                        lineHeight: 1.7,
                        margin: 0,
                        maxWidth: 760,
                        fontWeight: 300,
                    }}
                >
                    {a}
                </p>
            </div>
        </div>
    );
};

const Book = () => {
    return (
        <main data-testid="book-page">
            {/* Hero */}
            <section
                style={{
                    position: "relative",
                    paddingTop: 160,
                    paddingBottom: 60,
                    paddingLeft: 32,
                    paddingRight: 32,
                    overflow: "hidden",
                }}
                data-testid="book-hero"
            >
                <BackgroundRings opacity={0.06} position="right" />
                <div
                    style={{
                        maxWidth: 1200,
                        margin: "0 auto",
                        position: "relative",
                        zIndex: 2,
                    }}
                >
                    <FadeIn>
                        <p className="eyebrow" style={{ marginBottom: 24 }}>
                            Book a Session
                        </p>
                        <h1
                            className="font-serif"
                            data-testid="book-headline"
                            style={{
                                color: "#ffffff",
                                fontSize: "clamp(40px, 6.5vw, 88px)",
                                fontWeight: 400,
                                lineHeight: 1.05,
                                letterSpacing: "-0.02em",
                                marginBottom: 24,
                                maxWidth: 1000,
                            }}
                        >
                            Reserve your{" "}
                            <span style={{ color: "#c9a227" }}>
                                reset.
                            </span>
                        </h1>
                        <p
                            style={{
                                color: "#a8a8a8",
                                fontSize: 18,
                                lineHeight: 1.7,
                                maxWidth: 640,
                                fontWeight: 300,
                            }}
                        >
                            All sessions reserved through WhatsApp.
                        </p>
                    </FadeIn>
                </div>
            </section>

            {/* Session Types */}
            <section
                style={{
                    padding: "80px 32px 120px",
                }}
                data-testid="session-types-section"
            >
                <div style={{ maxWidth: 1400, margin: "0 auto" }}>
                    <FadeIn>
                        <p className="eyebrow" style={{ marginBottom: 16 }}>
                            Session Types
                        </p>
                        <h2
                            className="font-serif"
                            style={{
                                color: "#ffffff",
                                fontSize: "clamp(28px, 4vw, 48px)",
                                fontWeight: 400,
                                marginBottom: 56,
                                letterSpacing: "-0.02em",
                            }}
                        >
                            Choose by location.
                        </h2>
                    </FadeIn>
                    <div
                        style={{
                            display: "grid",
                            gridTemplateColumns:
                                "repeat(auto-fit, minmax(300px, 1fr))",
                            gap: 24,
                        }}
                    >
                        <FadeIn>
                            <LocationColumn
                                testId="book-location-rak"
                                title="Al Hamra"
                                location="Ras Al Khaimah"
                                message="Hi Aditya, I'd like to reserve a session at Al Hamra, Ras Al Khaimah (Book page)."
                                items={[
                                    {
                                        name: "Demo",
                                        note: "20–30 min",
                                    },
                                    "Single Session",
                                    "Group Session",
                                    "Kids Session",
                                    "Equine Session",
                                    "Camel Session",
                                    "Domestic Companion Session",
                                    "Corporate Session",
                                ]}
                            />
                        </FadeIn>
                        <FadeIn delay={120}>
                            <LocationColumn
                                testId="book-location-dubai"
                                title="Al Furjan"
                                location="Dubai"
                                message="Hi Aditya, I'd like to reserve a session at Al Furjan, Dubai (Book page)."
                                items={[
                                    "Single Session",
                                    "Group Session",
                                    "Corporate Session",
                                    "Private Session",
                                ]}
                            />
                        </FadeIn>
                        <FadeIn delay={240}>
                            <LocationColumn
                                testId="book-location-villa"
                                title="In-Villa"
                                location="UAE-wide"
                                message="Hi, I'd like to enquire about an In-Villa session UAE-wide (Book page)."
                                items={[
                                    {
                                        name: "Private HNWI Session",
                                        note: "90 min",
                                    },
                                    "Corporate Retreat",
                                    "Equine Session",
                                    "Camel Session",
                                    "Domestic Companion Session",
                                ]}
                            />
                        </FadeIn>
                    </div>
                </div>
            </section>

            {/* ANIMAL RECOVERY — Two Tiers */}
            <AnimalRecoverySection testIdPrefix="book-animal" />

            {/* How It Works */}
            <section
                style={{
                    padding: "120px 32px",
                    backgroundColor: "#070707",
                    borderTop: "1px solid rgba(201,162,39,0.1)",
                    borderBottom: "1px solid rgba(201,162,39,0.1)",
                }}
                data-testid="how-it-works-section"
            >
                <div style={{ maxWidth: 1100, margin: "0 auto" }}>
                    <FadeIn>
                        <p className="eyebrow" style={{ marginBottom: 20 }}>
                            How it Works
                        </p>
                        <h2
                            className="font-serif"
                            style={{
                                color: "#ffffff",
                                fontSize: "clamp(32px, 4.5vw, 56px)",
                                fontWeight: 400,
                                lineHeight: 1.1,
                                letterSpacing: "-0.02em",
                                marginBottom: 56,
                            }}
                        >
                            Three steps. That's all.
                        </h2>
                    </FadeIn>
                    <div
                        style={{
                            display: "grid",
                            gridTemplateColumns:
                                "repeat(auto-fit, minmax(260px, 1fr))",
                            gap: 1,
                            backgroundColor: "rgba(201,162,39,0.12)",
                        }}
                    >
                        {[
                            {
                                n: "01",
                                t: "Reserve via WhatsApp",
                                d: "We'll confirm your slot.",
                            },
                            {
                                n: "02",
                                t: "Arrive at your location",
                                d: "Al Hamra, Al Furjan, or your villa.",
                            },
                            {
                                n: "03",
                                t: "Lie down on the vibroacoustic bed. Reset.",
                                d: "20-30 minutes for demo. 60 minutes for full session. Leave lighter.",
                            },
                        ].map((s, i) => (
                            <FadeIn key={i} delay={i * 100}>
                                <div
                                    data-testid={`book-step-${i + 1}`}
                                    style={{
                                        backgroundColor: "#070707",
                                        padding: "44px 32px",
                                        height: "100%",
                                    }}
                                >
                                    <p
                                        className="font-serif"
                                        style={{
                                            color: "#c9a227",
                                            fontSize: 40,
                                            fontWeight: 400,
                                            marginBottom: 16,
                                        }}
                                    >
                                        {s.n}
                                    </p>
                                    <h3
                                        className="font-serif"
                                        style={{
                                            color: "#ffffff",
                                            fontSize: 24,
                                            fontWeight: 500,
                                            lineHeight: 1.25,
                                            marginBottom: 12,
                                        }}
                                    >
                                        {s.t}
                                    </h3>
                                    <p
                                        style={{
                                            color: "#888",
                                            fontSize: 15,
                                            lineHeight: 1.65,
                                            margin: 0,
                                        }}
                                    >
                                        {s.d}
                                    </p>
                                </div>
                            </FadeIn>
                        ))}
                    </div>
                </div>
            </section>

            {/* FAQ */}
            <section
                style={{
                    padding: "120px 32px",
                }}
                data-testid="faq-section"
            >
                <div style={{ maxWidth: 1000, margin: "0 auto" }}>
                    <FadeIn>
                        <p className="eyebrow" style={{ marginBottom: 20 }}>
                            FAQ
                        </p>
                        <h2
                            className="font-serif"
                            style={{
                                color: "#ffffff",
                                fontSize: "clamp(32px, 4.5vw, 56px)",
                                fontWeight: 400,
                                lineHeight: 1.1,
                                letterSpacing: "-0.02em",
                                marginBottom: 48,
                            }}
                        >
                            Practical details.
                        </h2>
                    </FadeIn>
                    {FAQ_ITEMS.map((item, i) => (
                        <FAQItem
                            key={i}
                            q={item.q}
                            a={item.a}
                            idx={i}
                        />
                    ))}
                </div>
            </section>

            {/* Reserve CTA */}
            <section
                style={{
                    position: "relative",
                    padding: "140px 32px",
                    borderTop: "1px solid rgba(201,162,39,0.15)",
                    overflow: "hidden",
                    textAlign: "center",
                }}
                data-testid="book-reserve-section"
            >
                <BackgroundRings opacity={0.07} position="center" />
                <div
                    style={{
                        maxWidth: 900,
                        margin: "0 auto",
                        position: "relative",
                        zIndex: 2,
                    }}
                >
                    <FadeIn>
                        <p className="eyebrow" style={{ marginBottom: 20 }}>
                            Limited Slots
                        </p>
                        <h2
                            className="font-serif"
                            style={{
                                color: "#ffffff",
                                fontSize: "clamp(32px, 5vw, 64px)",
                                fontWeight: 400,
                                lineHeight: 1.1,
                                letterSpacing: "-0.02em",
                                marginBottom: 24,
                            }}
                        >
                            15 retainer positions.
                        </h2>
                        <p
                            style={{
                                color: "#a8a8a8",
                                fontSize: 18,
                                lineHeight: 1.7,
                                marginBottom: 40,
                                fontWeight: 300,
                            }}
                        >
                            Limited slots available. Request access via
                            WhatsApp.
                        </p>
                        <a
                            href={waUrl(
                                "Hi Aditya, I'd like to request access — Reserve Now (Book page).",
                            )}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn-gold-solid"
                            data-testid="book-reserve-cta"
                            style={{ minWidth: 240 }}
                        >
                            Reserve Now
                        </a>
                    </FadeIn>
                </div>
            </section>
        </main>
    );
};

export default Book;
